import React, {Component} from 'react';
import {connect} from 'react-redux';
import RightMenu from './RightMenu';
import {NavLink as Link, withRouter} from 'react-router-dom';
import {Drawer, Button, Menu} from 'antd';
import {MenuOutlined, CloseOutlined} from '@ant-design/icons';
import styled, {keyframes} from 'styled-components';
import WaitlistBtn from '../../landing/WaitlistBtn';

import logo from '../../../logo.png';

// Animations
const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

// Styled Components
const ModernNavbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  padding: 1rem 2rem;
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeInDown} 0.6s ease-out;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    height: 40px;
    width: auto;
    filter: brightness(1.1);
  }
`;

const DesktopMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled(Button)`
  && {
    display: none;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    width: 48px;
    height: 48px;
    padding: 0;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(249, 115, 22, 0.5);
      transform: scale(1.05);
    }

    .anticon {
      color: white;
      font-size: 1.2rem;
    }

    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const ModernDrawer = styled(Drawer)`
  .ant-drawer-content {
    background: rgba(10, 10, 10, 0.95);
    backdrop-filter: blur(20px);
  }

  .ant-drawer-header {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1.5rem;
  }

  .ant-drawer-title {
    color: white;
    font-family: 'Hanson Bold', 'Inter', system-ui, sans-serif;
    font-size: 1.25rem;
  }

  .ant-drawer-close {
    color: white;
    font-size: 1.2rem;

    &:hover {
      color: #f97316;
    }
  }

  .ant-drawer-body {
    padding: 2rem 1.5rem;
    background: transparent;
  }
`;

const MobileMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: ${slideIn} 0.3s ease-out;
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }

  &.active {
    color: #f97316;
    background: rgba(249, 115, 22, 0.1);
  }
`;

class NavBar extends Component {
  state = {
    current: 'mail',
    visible: false,
  };
  showDrawer = () => {
    this.setState({
      visible: true,
    });
  };

  onClose = () => {
    this.setState({
      visible: false,
    });
  };

  renderNavContent() {
    const isAuthPage = [
      '/creative/login',
      '/creative/signup',
      '/sign-up',
      '/login',
      '/brand/login',
      '/brand/signup',
      '/on-boarding-flow/0',
      '/on-boarding-flow/1',
      '/on-boarding-flow/2',
      '/on-boarding-flow/3',
      '/on-boarding-flow/4',
    ].includes(this.props.location.pathname);

    const isLandingPage = this.props.location.pathname === '/' && !this.props.authenticated;

    if (isAuthPage) {
      return (
        <NavContainer>
          <LogoContainer to="/">
            <img src={logo} alt="CLLCTVE Logo" />
          </LogoContainer>
        </NavContainer>
      );
    }

    if (isLandingPage) {
      return (
        <NavContainer>
          <LogoContainer to="/">
            <img src={logo} alt="CLLCTVE Logo" />
          </LogoContainer>

          <DesktopMenu>
            <WaitlistBtn />
          </DesktopMenu>

          <MobileMenuButton onClick={this.showDrawer}>
            <MenuOutlined />
          </MobileMenuButton>

          <ModernDrawer
            title="Welcome to CLLCTVE"
            placement="right"
            closable={true}
            onClose={this.onClose}
            visible={this.state.visible}
            width={320}
          >
            <MobileMenuContainer>
              <WaitlistBtn />
            </MobileMenuContainer>
          </ModernDrawer>
        </NavContainer>
      );
    }

    // Authenticated user navbar
    return (
      <NavContainer>
        <LogoContainer to="/">
          <img src={logo} alt="CLLCTVE Logo" />
        </LogoContainer>

        <DesktopMenu>
          <RightMenu />
        </DesktopMenu>

        <MobileMenuButton onClick={this.showDrawer}>
          <MenuOutlined />
        </MobileMenuButton>

        <ModernDrawer
          title="Menu"
          placement="right"
          closable={true}
          onClose={this.onClose}
          visible={this.state.visible}
          width={320}
        >
          <MobileMenuContainer>
            <RightMenu isMobile={true} />
          </MobileMenuContainer>
        </ModernDrawer>
      </NavContainer>
    );
  }

  render() {
    return <ModernNavbar>{this.renderNavContent()}</ModernNavbar>;
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    authenticated: state.auth.authenticated,
  };
};

export default withRouter(connect(mapStateToProps)(NavBar));
